import { useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import axios from "axios";
import {
  Globe, Plus, Trash2, Pencil, ExternalLink, Wifi, WifiOff,
  Sparkles, DollarSign, FolderOpen, Eye, Loader2, ChevronRight,
  Tag, MapPin, Users, BarChart3, Link2, FileText, RefreshCw, Search
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DIRECTORIES = [
  "Katalog Stron Premium", "SEO Directory PL", "Polskie Strony WWW",
  "Katalog Firm Online", "Web Directory EU", "Link Exchange PL"
];

export default function Sites() {
  const { t } = useLanguage();
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [detailSite, setDetailSite] = useState(null);
  const [detailTab, setDetailTab] = useState("overview");
  const [categories, setCategories] = useState([]);
  const [regions, setRegions] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionResult, setConnectionResult] = useState(null);
  const [searchQuery, setSearchQuery] = useState(""); // Wyszukiwanie
  const [form, setForm] = useState({
    name: "", url: "", site_type: "wordpress", status: "active",
    ftp_host: "", ftp_user: "", ftp_pass: "",
    wp_api_url: "", wp_api_user: "", wp_api_pass: "",
    emergent_api_url: "", emergent_api_key: "",
  });

  // Filtrowane strony
  const filteredSites = sites.filter(site => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      (site.name || "").toLowerCase().includes(query) ||
      (site.url || "").toLowerCase().includes(query)
    );
  });

  const fetchSites = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sites`);
      setSites(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sites/wp/categories`);
      setCategories(res.data.categories || []);
      setRegions(res.data.regions || []);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchSites(); fetchCategories(); }, [fetchSites, fetchCategories]);

  const resetForm = () => {
    setForm({ name: "", url: "", site_type: "wordpress", status: "active",
      ftp_host: "", ftp_user: "", ftp_pass: "",
      wp_api_url: "", wp_api_user: "", wp_api_pass: "",
      emergent_api_url: "", emergent_api_key: "" });
    setEditingSite(null);
    setConnectionResult(null);
  };

  const handleSave = async () => {
    // Walidacja - sprawdź czy domena już istnieje
    const urlNormalized = form.url.toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '');
    const existingDomain = sites.find(site => {
      if (editingSite && site.id === editingSite.id) return false; // Pomiń edytowany
      const siteUrl = (site.url || "").toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '');
      return siteUrl === urlNormalized;
    });
    
    if (existingDomain) {
      toast.error(`Domena "${urlNormalized}" już istnieje w systemie!`);
      return;
    }
    
    try {
      if (editingSite) {
        await axios.put(`${API}/sites/${editingSite.id}`, form);
      } else {
        await axios.post(`${API}/sites`, form);
      }
      toast.success(t("common.success"));
      fetchSites();
      setDialogOpen(false);
      resetForm();
    } catch (err) { 
      if (err.response?.data?.detail?.includes('duplikat') || err.response?.data?.detail?.includes('exists')) {
        toast.error("Ta domena już istnieje!");
      } else {
        toast.error(t("common.error")); 
      }
    }
  };

  const handleEdit = (site) => {
    setEditingSite(site);
    setForm({
      name: site.name, url: site.url, site_type: site.site_type, status: site.status,
      ftp_host: site.ftp_host || "", ftp_user: site.ftp_user || "", ftp_pass: site.ftp_pass || "",
      wp_api_url: site.wp_api_url || "", wp_api_user: site.wp_api_user || "", wp_api_pass: site.wp_api_pass || "",
      emergent_api_url: site.emergent_api_url || "", emergent_api_key: site.emergent_api_key || "",
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/sites/${id}`);
      toast.success(t("common.success"));
      fetchSites();
      if (detailSite?.id === id) setDetailSite(null);
    } catch (err) { toast.error(t("common.error")); }
  };

  // Test polaczenia WP
  const handleTestConnection = async (url) => {
    setTestingConnection(true);
    setConnectionResult(null);
    try {
      const res = await axios.post(`${API}/sites/wp/test`, { url });
      setConnectionResult(res.data);
      toast[res.data.success ? "success" : "error"](
        res.data.success ? t("wp.connectionOk") : (res.data.error || t("wp.connectionFail"))
      );
    } catch (err) {
      setConnectionResult({ success: false, error: "Blad testu" });
      toast.error(t("wp.connectionFail"));
    } finally { setTestingConnection(false); }
  };

  // AI analiza strony
  const handleAnalyze = async (site) => {
    setAnalyzing(true);
    try {
      const res = await axios.post(`${API}/sites/wp/${site.id}/analyze`, { ai_model: "gemini" });
      toast.success(t("wp.analysisComplete"));
      fetchSites();
      // Odswiez detail
      const updated = await axios.get(`${API}/sites/${site.id}`);
      setDetailSite(updated.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally { setAnalyzing(false); }
  };

  // Generuj cennik
  const handlePricing = async (site) => {
    setPricingLoading(true);
    try {
      const res = await axios.post(`${API}/sites/wp/${site.id}/pricing`, { ai_model: "gemini" });
      toast.success(t("wp.pricingGenerated"));
      fetchSites();
      const updated = await axios.get(`${API}/sites/${site.id}`);
      setDetailSite(updated.data);
    } catch (err) {
      toast.error(t("common.error"));
    } finally { setPricingLoading(false); }
  };

  // Wyslij do katalogow
  const handleDirectories = async (site, selectedDirs) => {
    try {
      await axios.post(`${API}/sites/wp/${site.id}/directories`, { directories: selectedDirs });
      toast.success(t("wp.directoriesSubmitted"));
      fetchSites();
      const updated = await axios.get(`${API}/sites/${site.id}`);
      setDetailSite(updated.data);
    } catch (err) { toast.error(t("common.error")); }
  };

  // Aktualizuj kategorie/region
  const handleUpdateExtra = async (siteId, key, value) => {
    try {
      await axios.patch(`${API}/sites/${siteId}/extra`, { [key]: value });
      fetchSites();
      const updated = await axios.get(`${API}/sites/${siteId}`);
      setDetailSite(updated.data);
    } catch (err) { toast.error(t("common.error")); }
  };

  const openDetail = (site) => {
    setDetailSite(site);
    setDetailTab("overview");
  };

  const wpSites = filteredSites.filter(s => s.site_type === "wordpress");
  const htmlSites = filteredSites.filter(s => s.site_type === "html");
  const emergentSites = filteredSites.filter(s => s.site_type === "emergent");

  // Test Emergent connection
  const handleTestEmergentConnection = async (url) => {
    setTestingConnection(true);
    setConnectionResult(null);
    try {
      const res = await axios.post(`${API}/sites/emergent/test`, { 
        url, 
        api_key: form.emergent_api_key 
      });
      setConnectionResult(res.data);
    } catch (err) {
      setConnectionResult({ success: false, error: err.response?.data?.detail || err.message });
    } finally {
      setTestingConnection(false);
    }
  };

  return (
    <div data-testid="sites-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-foreground">
            {t("sites.title")}
          </h1>
          <p className="text-sm text-muted-foreground mt-1 font-body">{t("sites.subtitle")}</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Wyszukiwarka */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input 
              data-testid="search-sites-input"
              placeholder="Szukaj strony..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-64"
            />
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-site-btn" className="rounded-lg gap-2">
                <Plus className="w-4 h-4" strokeWidth={1.5} />
                {t("sites.addSite")}
              </Button>
            </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingSite ? t("sites.edit") : t("sites.addSite")}
              </DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("sites.name")}</Label>
                  <Input data-testid="site-name-input" value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Moja strona" />
                </div>
                <div className="space-y-2">
                  <Label>{t("sites.url")} <span className="text-xs text-muted-foreground">(publiczna domena gdzie czytelnicy widzą posty)</span></Label>
                  <Input data-testid="site-url-input" value={form.url}
                    onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://moja-strona.pl" />
                  <p className="text-[10px] text-muted-foreground">
                    NIE wpisuj subdomeny API (np. <code>api.moja-strona.pl</code>) — to inne pole niżej. Tu wpisz adres pod którym otwierają się posty (np. <code>https://pomoc112.pl</code>). Search Console + sitemap działają na tej domenie.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("sites.type")}</Label>
                  <Select value={form.site_type} onValueChange={(v) => setForm({ ...form, site_type: v })}>
                    <SelectTrigger data-testid="site-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="wordpress">{t("sites.wordpress")}</SelectItem>
                      <SelectItem value="html">{t("sites.html")}</SelectItem>
                      <SelectItem value="emergent">Emergent App</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t("sites.status")}</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger data-testid="site-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">{t("sites.active")}</SelectItem>
                      <SelectItem value="inactive">{t("sites.inactive")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {form.site_type === "wordpress" && (
                <div className="grid gap-3 p-3 rounded-lg bg-muted/50">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">WordPress API</p>
                    {form.url && (
                      <Button variant="ghost" size="sm" className="h-7 text-xs gap-1"
                        data-testid="test-connection-btn"
                        onClick={() => handleTestConnection(form.url)}
                        disabled={testingConnection}>
                        {testingConnection ? <Loader2 className="w-3 h-3 animate-spin" /> :
                          connectionResult?.success ? <Wifi className="w-3 h-3 text-emerald-500" /> :
                          <WifiOff className="w-3 h-3" />}
                        {t("wp.testConnection")}
                      </Button>
                    )}
                  </div>
                  {connectionResult && (
                    <div className={`p-2 rounded-md text-xs ${connectionResult.success ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400" : "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400"}`}>
                      {connectionResult.success ? (
                        <span>{t("wp.connectionOk")} — {connectionResult.site_name}, {connectionResult.posts_count} {t("wp.posts")}</span>
                      ) : (
                        <span>{connectionResult.error || t("wp.connectionFail")}</span>
                      )}
                    </div>
                  )}
                  <Input placeholder="Username (np. epidemos)" value={form.wp_api_user}
                    onChange={(e) => setForm({ ...form, wp_api_user: e.target.value })} />
                  <Input type="password" placeholder="Application Password (WordPress 5.6+)" value={form.wp_api_pass}
                    onChange={(e) => setForm({ ...form, wp_api_pass: e.target.value })} />
                  <div className="space-y-1">
                    <Input placeholder="API base URL (opcjonalne — auto: {url}/wp-json/wp/v2)" value={form.wp_api_url}
                      onChange={(e) => setForm({ ...form, wp_api_url: e.target.value })} />
                    <p className="text-[10px] text-muted-foreground">
                      Wpisz tylko jeśli agent dał własny endpoint inny niż <code>{(form.url || "https://example.com").replace(/\/$/, "")}/wp-json/wp/v2</code>.
                      Przykład: <code>https://api.moja-strona.pl/wp-json/wp/v2</code>
                    </p>
                  </div>
                  <div className="text-xs bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 p-2 rounded border border-blue-200">
                    📖 Pełna dokumentacja API: <a href="/api-docs" target="_blank" className="underline font-medium">/api-docs</a> · 🧪 Sandbox testowy: <a href="/wydawcy-testy" target="_blank" className="underline font-medium">/wydawcy-testy</a>
                  </div>
                </div>
              )}
              {form.site_type === "emergent" && (
                <div className="grid gap-3 p-3 rounded-lg bg-gradient-to-br from-violet-50 to-indigo-50 dark:from-violet-900/20 dark:to-indigo-900/20 border border-violet-200 dark:border-violet-800">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold text-violet-700 dark:text-violet-300 uppercase tracking-wider flex items-center gap-2">
                      <Sparkles className="w-3.5 h-3.5" />
                      Emergent API
                    </p>
                    {form.url && (
                      <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 text-violet-600 hover:text-violet-700"
                        data-testid="test-emergent-btn"
                        onClick={() => handleTestEmergentConnection(form.url)}
                        disabled={testingConnection}>
                        {testingConnection ? <Loader2 className="w-3 h-3 animate-spin" /> :
                          connectionResult?.success ? <Wifi className="w-3 h-3 text-emerald-500" /> :
                          <WifiOff className="w-3 h-3" />}
                        Test połączenia
                      </Button>
                    )}
                  </div>
                  {connectionResult && (
                    <div className={`p-2 rounded-md text-xs ${connectionResult.success ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400" : "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400"}`}>
                      {connectionResult.success ? (
                        <div>
                          <span className="font-medium">{connectionResult.message}</span>
                          {connectionResult.endpoints && (
                            <div className="mt-1 flex gap-2">
                              {connectionResult.endpoints.posts && <Badge variant="secondary" className="text-[10px]">Posts: {connectionResult.endpoints.posts_count || 0}</Badge>}
                              {connectionResult.endpoints.products && <Badge variant="secondary" className="text-[10px]">Products: {connectionResult.endpoints.products_count || 0}</Badge>}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span>{connectionResult.error || "Błąd połączenia"}</span>
                      )}
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] text-muted-foreground">
                      Dla stron stworzonych przez app.emergent.sh
                    </p>
                    <a 
                      href="https://epidemos.pl/api/sites/emergent/docs" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-[10px] text-violet-600 hover:text-violet-700 dark:text-violet-400 hover:underline flex items-center gap-1"
                    >
                      <FileText className="w-3 h-3" />
                      Dokumentacja API
                    </a>
                  </div>
                  <Input placeholder="Klucz API (opcjonalny)" value={form.emergent_api_key}
                    onChange={(e) => setForm({ ...form, emergent_api_key: e.target.value })} />
                </div>
              )}
              {form.site_type === "html" && (
                <div className="grid gap-3 p-3 rounded-lg bg-muted/50">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">FTP</p>
                  <Input placeholder={t("sites.ftpHost")} value={form.ftp_host}
                    onChange={(e) => setForm({ ...form, ftp_host: e.target.value })} />
                  <div className="grid grid-cols-2 gap-3">
                    <Input placeholder={t("sites.ftpUser")} value={form.ftp_user}
                      onChange={(e) => setForm({ ...form, ftp_user: e.target.value })} />
                    <Input type="password" placeholder={t("sites.ftpPass")} value={form.ftp_pass}
                      onChange={(e) => setForm({ ...form, ftp_pass: e.target.value })} />
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>{t("sites.cancel")}</Button>
              <Button data-testid="save-site-btn" onClick={handleSave}>{t("sites.save")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lista stron */}
        <div className={detailSite ? "lg:col-span-1" : "lg:col-span-3"}>
          <Card className="rounded-xl border shadow-sm bg-card">
            <CardHeader className="pb-2 pt-4 px-5">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-heading flex items-center gap-2 text-foreground">
                  <Globe className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                  {t("sites.title")}
                </CardTitle>
                <div className="flex gap-1.5">
                  <Badge variant="secondary" className="text-[10px] rounded-md">{wpSites.length} WP</Badge>
                  <Badge variant="outline" className="text-[10px] rounded-md">{htmlSites.length} HTML</Badge>
                  {emergentSites.length > 0 && (
                    <Badge className="text-[10px] rounded-md bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">{emergentSites.length} Emergent</Badge>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-5 pb-4 pt-1">
              {filteredSites.length === 0 ? (
                <div data-testid="no-sites" className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <Globe className="w-10 h-10 mb-3 opacity-20" strokeWidth={1} />
                  <p className="text-xs">{searchQuery ? "Nie znaleziono stron" : t("sites.noSites")}</p>
                </div>
              ) : (
                <ScrollArea className="h-[calc(100vh-220px)] max-h-[700px]">
                  <div className="space-y-1.5 pr-3">
                    {filteredSites.map((site) => (
                      <button
                        key={site.id}
                        data-testid={`site-item-${site.id}`}
                        onClick={() => openDetail(site)}
                        className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-all ${
                          detailSite?.id === site.id
                            ? "border-primary bg-primary/5 shadow-sm"
                            : "border-border hover:border-primary/30 hover:bg-accent/30"
                        }`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">{site.name}</p>
                          <p className="text-[11px] text-muted-foreground truncate">{site.url}</p>
                          {site.publisher_id && (
                            <p className="text-[9px] text-violet-500 mt-0.5">Wydawca: {site.publisher_name || "—"}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <Badge variant="secondary" className={`text-[9px] rounded-md ${site.site_type === "emergent" ? "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400" : ""}`}>
                            {site.site_type === "wordpress" ? "WP" : site.site_type === "emergent" ? "EMG" : "HTML"}
                          </Badge>
                          {site.publisher_id && (
                            <Badge className="bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400 text-[8px] border-0 rounded-md">Wydawca</Badge>
                          )}
                          {site.category && (
                            <Badge variant="outline" className="text-[9px] rounded-md">{site.category}</Badge>
                          )}
                          <div className={`w-2 h-2 rounded-full ${site.status === "active" ? "bg-emerald-500" : "bg-slate-300"}`} />
                        </div>
                      </button>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Detail Panel */}
        {detailSite && (
          <div className="lg:col-span-2">
            <Card className="rounded-xl border shadow-sm bg-card">
              <CardHeader className="pb-2 pt-4 px-5">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base font-heading text-foreground">{detailSite.name}</CardTitle>
                    <a href={detailSite.url} target="_blank" rel="noopener noreferrer"
                      className="text-xs text-primary hover:underline flex items-center gap-1 mt-0.5">
                      {detailSite.url} <ExternalLink className="w-2.5 h-2.5" />
                    </a>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(detailSite)}>
                      <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(detailSite.id)}>
                      <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="px-5 pb-5 pt-2">
                <Tabs value={detailTab} onValueChange={setDetailTab}>
                  <TabsList data-testid="site-detail-tabs" className="mb-4">
                    <TabsTrigger value="overview" className="text-xs">{t("wp.overview")}</TabsTrigger>
                    <TabsTrigger value="analysis" className="text-xs">{t("wp.aiAnalysis")}</TabsTrigger>
                    <TabsTrigger value="pricing" className="text-xs">{t("wp.pricing")}</TabsTrigger>
                    <TabsTrigger value="directories" className="text-xs">{t("wp.directories")}</TabsTrigger>
                  </TabsList>

                  {/* ===== PRZEGLAD ===== */}
                  <TabsContent value="overview">
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {[
                          { icon: Tag, label: t("wp.category"), value: detailSite.category || "—" },
                          { icon: MapPin, label: t("wp.region"), value: detailSite.region || "—" },
                          { icon: Users, label: t("wp.audience"), value: detailSite.target_audience?.substring(0, 20) || "—" },
                          { icon: BarChart3, label: t("wp.traffic"), value: detailSite.monthly_visitors ? `~${detailSite.monthly_visitors}/m` : "—" },
                        ].map((item, i) => (
                          <div key={i} className="p-3 rounded-lg bg-muted/50">
                            <item.icon className="w-4 h-4 text-muted-foreground mb-1.5" strokeWidth={1.5} />
                            <p className="text-xs font-semibold text-foreground">{item.value}</p>
                            <p className="text-[10px] text-muted-foreground">{item.label}</p>
                          </div>
                        ))}
                      </div>

                      {/* Wybor kategorii i regionu */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label className="text-xs">{t("wp.assignCategory")}</Label>
                          <Select value={detailSite.category || ""} onValueChange={(v) => handleUpdateExtra(detailSite.id, "category", v)}>
                            <SelectTrigger data-testid="site-category-select" className="h-9 text-xs">
                              <SelectValue placeholder="—" />
                            </SelectTrigger>
                            <SelectContent>
                              {categories.map(c => <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs">{t("wp.assignRegion")}</Label>
                          <Select value={detailSite.region || ""} onValueChange={(v) => handleUpdateExtra(detailSite.id, "region", v)}>
                            <SelectTrigger data-testid="site-region-select" className="h-9 text-xs">
                              <SelectValue placeholder="—" />
                            </SelectTrigger>
                            <SelectContent>
                              {regions.map(r => <SelectItem key={r} value={r} className="text-xs">{r}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-3 pt-1">
                        <div className="p-3 rounded-lg bg-muted/50 text-center">
                          <p className="text-lg font-bold font-heading text-foreground">{detailSite.posts_count || 0}</p>
                          <p className="text-[10px] text-muted-foreground">{t("wp.publishedArticles")}</p>
                        </div>
                        <div className="p-3 rounded-lg bg-muted/50 text-center">
                          <p className="text-lg font-bold font-heading text-foreground">{detailSite.backlinks_count || 0}</p>
                          <p className="text-[10px] text-muted-foreground">{t("wp.backlinks")}</p>
                        </div>
                        <div className="p-3 rounded-lg bg-muted/50 text-center">
                          <p className="text-lg font-bold font-heading text-foreground">{(detailSite.directories || []).length}</p>
                          <p className="text-[10px] text-muted-foreground">{t("wp.directories")}</p>
                        </div>
                      </div>

                      {/* Zorki flag — spolecznosc otrzymuje "zorki" na tym portalu */}
                      <label className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 cursor-pointer hover:bg-amber-100/50 dark:hover:bg-amber-950/40 transition-colors">
                        <input
                          type="checkbox"
                          data-testid="site-zorki-toggle"
                          checked={!!detailSite.zorki_enabled}
                          onChange={(e) => handleUpdateExtra(detailSite.id, "zorki_enabled", e.target.checked)}
                          className="w-4 h-4 rounded border-amber-400 text-amber-600 focus:ring-amber-500 cursor-pointer"
                        />
                        <div className="flex-1">
                          <p className="text-xs font-semibold text-amber-900 dark:text-amber-100">⭐ Społeczność otrzymuje "zorki"</p>
                          <p className="text-[10px] text-amber-700 dark:text-amber-300">Zaznacz, jeśli użytkownicy tego portalu zbierają "zorki" (dla łączenia międzyportalowego)</p>
                        </div>
                      </label>

                      {detailSite.site_type === "wordpress" && (
                        <Button variant="outline" size="sm" data-testid="test-wp-btn" className="gap-2 text-xs"
                          onClick={() => handleTestConnection(detailSite.url)} disabled={testingConnection}>
                          {testingConnection ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> :
                           <Wifi className="w-3.5 h-3.5" strokeWidth={1.5} />}
                          {t("wp.testConnection")}
                        </Button>
                      )}
                      {detailSite.site_type === "wordpress" && (
                        <Button variant="outline" size="sm" data-testid="test-publish-btn" className="gap-2 text-xs"
                          onClick={async () => {
                            try {
                              const res = await axios.post(`${API}/sites/wp/${detailSite.id}/test-publish`);
                              if (res.data.success) {
                                toast.success("Testowy artykul opublikowany na WP!");
                              } else {
                                toast.error(res.data.error || "Blad publikacji");
                              }
                              fetchSites();
                              const updated = await axios.get(`${API}/sites/${detailSite.id}`);
                              setDetailSite(updated.data);
                            } catch (err) { toast.error("Blad testu publikacji"); }
                          }}>
                          <FileText className="w-3.5 h-3.5" strokeWidth={1.5} />
                          Test publikacji WP
                        </Button>
                      )}
                      {detailSite.last_wp_test && (
                        <p className="text-[10px] text-muted-foreground">
                          Ostatni test WP: {new Date(detailSite.last_wp_test).toLocaleString()}
                          {detailSite.wp_test_result?.success ? " - OK" : " - BLAD"}
                        </p>
                      )}
                    </div>
                  </TabsContent>

                  {/* ===== ANALIZA AI ===== */}
                  <TabsContent value="analysis">
                    <div className="space-y-4">
                      <Button data-testid="analyze-site-btn" onClick={() => handleAnalyze(detailSite)}
                        disabled={analyzing} className="gap-2 rounded-lg">
                        {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> :
                         <Sparkles className="w-4 h-4" strokeWidth={1.5} />}
                        {analyzing ? t("wp.analyzing") : t("wp.runAnalysis")}
                      </Button>

                      {detailSite.ai_analysis && (
                        <div className="space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            <div className="p-3 rounded-lg bg-muted/50">
                              <p className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wider">{t("wp.categories")}</p>
                              <div className="flex flex-wrap gap-1 mt-1.5">
                                {(detailSite.ai_analysis.categories || []).map((c, i) => (
                                  <Badge key={i} variant="secondary" className="text-[10px] rounded-md">{c}</Badge>
                                ))}
                              </div>
                            </div>
                            <div className="p-3 rounded-lg bg-muted/50">
                              <p className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wider">{t("wp.audience")}</p>
                              <p className="text-xs text-foreground mt-1.5">{detailSite.ai_analysis.target_audience}</p>
                            </div>
                          </div>

                          {detailSite.ai_analysis.strengths && (
                            <div className="p-3 rounded-lg bg-muted/50">
                              <p className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wider mb-1.5">{t("wp.strengths")}</p>
                              {detailSite.ai_analysis.strengths.map((s, i) => (
                                <p key={i} className="text-xs text-foreground py-0.5 pl-3 border-l-2 border-emerald-400">{s}</p>
                              ))}
                            </div>
                          )}

                          {detailSite.ai_analysis.weaknesses?.length > 0 && (
                            <div className="p-3 rounded-lg bg-muted/50">
                              <p className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wider mb-1.5">Slabe strony / Do poprawy</p>
                              {detailSite.ai_analysis.weaknesses.map((w, i) => (
                                <p key={i} className="text-xs text-foreground py-0.5 pl-3 border-l-2 border-red-400">{w}</p>
                              ))}
                            </div>
                          )}

                          {detailSite.ai_analysis.top_search_phrases?.length > 0 && (
                            <div className="p-3 rounded-lg bg-muted/50">
                              <p className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wider mb-1.5">TOP frazy wyszukiwania Google</p>
                              <div className="space-y-1">
                                {detailSite.ai_analysis.top_search_phrases.map((phrase, i) => (
                                  <div key={i} className="flex items-center gap-2">
                                    <span className="text-[9px] font-bold text-muted-foreground w-4">{i+1}.</span>
                                    <Badge variant="outline" className="text-[10px] rounded-md">{phrase}</Badge>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {detailSite.ai_analysis.suggested_topics && (
                            <div className="p-3 rounded-lg bg-muted/50">
                              <p className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wider mb-1.5">Propozycje 10 tematow artykulow</p>
                              {detailSite.ai_analysis.suggested_topics.map((topic, i) => (
                                <p key={i} className="text-xs text-foreground py-0.5 pl-3 border-l-2 border-primary/40">{i+1}. {topic}</p>
                              ))}
                            </div>
                          )}

                          {detailSite.ai_analysis.quick_wins_seo?.length > 0 && (
                            <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/10">
                              <p className="text-[10px] uppercase font-semibold text-amber-700 dark:text-amber-400 tracking-wider mb-1.5">Quick Wins SEO</p>
                              {detailSite.ai_analysis.quick_wins_seo.map((qw, i) => (
                                <p key={i} className="text-xs text-foreground py-0.5 pl-3 border-l-2 border-amber-400">{qw}</p>
                              ))}
                            </div>
                          )}

                          {detailSite.ai_analysis.keywords && (
                            <div className="p-3 rounded-lg bg-muted/50">
                              <p className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wider mb-1.5">{t("wp.seoKeywords")}</p>
                              <div className="flex flex-wrap gap-1">
                                {detailSite.ai_analysis.keywords.map((kw, i) => (
                                  <Badge key={i} variant="outline" className="text-[10px] rounded-md">{kw}</Badge>
                                ))}
                              </div>
                            </div>
                          )}

                          {detailSite.ai_analysis.seo_strategies && (
                            <div className="p-3 rounded-lg bg-muted/50">
                              <p className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wider mb-1.5">{t("wp.seoStrategies")}</p>
                              {detailSite.ai_analysis.seo_strategies.map((s, i) => (
                                <p key={i} className="text-xs text-foreground py-0.5 pl-3 border-l-2 border-amber-400">{s}</p>
                              ))}
                            </div>
                          )}

                          {detailSite.ai_analysis.long_tail_keywords?.length > 0 && (
                            <div className="p-3 rounded-lg bg-muted/50">
                              <p className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wider mb-1.5">Long-tail keywords</p>
                              <div className="flex flex-wrap gap-1">
                                {detailSite.ai_analysis.long_tail_keywords.map((kw, i) => (
                                  <Badge key={i} variant="secondary" className="text-[9px] rounded-md">{kw}</Badge>
                                ))}
                              </div>
                            </div>
                          )}

                          {detailSite.ai_analysis.competitors?.length > 0 && (
                            <div className="p-3 rounded-lg bg-muted/50">
                              <p className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wider mb-1.5">Konkurencja w niszy</p>
                              {detailSite.ai_analysis.competitors.map((c, i) => (
                                <p key={i} className="text-xs text-foreground py-0.5 pl-3 border-l-2 border-slate-300">{c}</p>
                              ))}
                            </div>
                          )}

                          <p className="text-[10px] text-muted-foreground/60">
                            {t("wp.analyzedAt")}: {detailSite.ai_analysis_at ? new Date(detailSite.ai_analysis_at).toLocaleString() : "—"}
                          </p>
                        </div>
                      )}
                    </div>
                  </TabsContent>

                  {/* ===== CENNIK REKLAM (edytowalny) ===== */}
                  <TabsContent value="pricing">
                    <EditablePricing
                      site={detailSite}
                      onGenerate={() => handlePricing(detailSite)}
                      pricingLoading={pricingLoading}
                      onSave={async (pricingData) => {
                        try {
                          await axios.put(`${API}/sites/wp/${detailSite.id}/pricing`, { pricing: pricingData });
                          toast.success(t("common.success"));
                          fetchSites();
                          const updated = await axios.get(`${API}/sites/${detailSite.id}`);
                          setDetailSite(updated.data);
                        } catch (err) { toast.error(t("common.error")); }
                      }}
                      t={t}
                    />
                  </TabsContent>

                  {/* ===== KATALOGI ===== */}
                  <TabsContent value="directories">
                    <DirectoriesPanel
                      site={detailSite}
                      onSubmit={(dirs) => handleDirectories(detailSite, dirs)}
                      t={t}
                    />
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}

// Podkomponent katalogow
function DirectoriesPanel({ site, onSubmit, t }) {
  const [selected, setSelected] = useState([]);

  const toggle = (dir) => {
    setSelected(prev => prev.includes(dir) ? prev.filter(d => d !== dir) : [...prev, dir]);
  };

  const submitted = site.directories || [];

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">{t("wp.directoriesDesc")}</p>

      <div className="grid grid-cols-2 gap-2">
        {DIRECTORIES.map((dir) => {
          const alreadySent = submitted.some(s => s.directory === dir);
          return (
            <button
              key={dir}
              data-testid={`dir-${dir.replace(/\s/g, '-')}`}
              disabled={alreadySent}
              onClick={() => toggle(dir)}
              className={`p-3 rounded-lg border text-left text-xs transition-all ${
                alreadySent ? "border-emerald-200 bg-emerald-50 dark:bg-emerald-900/10 dark:border-emerald-800 opacity-70" :
                selected.includes(dir) ? "border-primary bg-primary/5 shadow-sm" :
                "border-border hover:border-primary/30"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-foreground">{dir}</span>
                {alreadySent && <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 text-[9px] border-0 rounded-md">{t("wp.sent")}</Badge>}
              </div>
            </button>
          );
        })}
      </div>

      {selected.length > 0 && (
        <Button data-testid="submit-directories-btn" onClick={() => { onSubmit(selected); setSelected([]); }}
          className="gap-2 rounded-lg">
          <FolderOpen className="w-4 h-4" strokeWidth={1.5} />
          {t("wp.submitDirectories")} ({selected.length})
        </Button>
      )}
    </div>
  );
}

// Edytowalny cennik - inspirowany screenami ze starego systemu
function EditablePricing({ site, onGenerate, pricingLoading, onSave, t }) {
  const [editing, setEditing] = useState(false);
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [wpCategories, setWpCategories] = useState([]);
  const [loadingCats, setLoadingCats] = useState(false);

  useEffect(() => {
    if (site.pricing?.pricing) {
      setItems(site.pricing.pricing.map(item => ({
        ...item,
        enabled: item.enabled !== false,
        show_in_shop: item.show_in_shop !== false,
        wp_category_id: item.wp_category_id || "",
        wp_category_name: item.wp_category_name || "",
        wp_category_ids: item.wp_category_ids || (item.wp_category_id ? [item.wp_category_id] : []),
        wp_category_names: item.wp_category_names || (item.wp_category_name ? [item.wp_category_name] : []),
      })));
    }
  }, [site.pricing]);

  // Pobierz kategorie WP przy edycji
  const loadWpCategories = async (force = false) => {
    if (wpCategories.length > 0 && !force) return;
    setLoadingCats(true);
    try {
      const res = await axios.get(`${API}/sites/wp/${site.id}/wp-categories`);
      setWpCategories(res.data || []);
    } catch (err) { console.error(err); }
    finally { setLoadingCats(false); }
  };

  // Wymuś pełną aktualizację kategorii z WordPress lub Emergent API
  const refreshCategories = async () => {
    setLoadingCats(true);
    try {
      const res = await axios.post(`${API}/sites/wp/${site.id}/refresh-categories`);
      if (res.data?.categories) {
        setWpCategories(res.data.categories);
        const source = res.data.source === "emergent_api" ? "API strony" : "WordPress";
        toast.success(`Pobrano ${res.data.count} kategorii z ${source}`);
      }
    } catch (err) { 
      console.error(err); 
      toast.error("Błąd aktualizacji kategorii");
    }
    finally { setLoadingCats(false); }
  };

  const startEditing = () => {
    setEditing(true);
    loadWpCategories();
  };

  const updatePrice = (index, newPrice) => {
    setItems(prev => prev.map((item, i) => i === index ? { ...item, price_pln: parseFloat(newPrice) || 0 } : item));
  };

  const toggleEnabled = (index) => {
    setItems(prev => prev.map((item, i) => i === index ? { ...item, enabled: !item.enabled } : item));
  };

  const toggleShop = (index) => {
    setItems(prev => prev.map((item, i) => i === index ? { ...item, show_in_shop: !item.show_in_shop } : item));
  };

  const handleSave = async () => {
    setSaving(true);
    await onSave({ ...site.pricing, pricing: items });
    setSaving(false);
    setEditing(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button data-testid="generate-pricing-btn" onClick={onGenerate} disabled={pricingLoading}
          variant="outline" className="gap-2 rounded-lg text-xs">
          {pricingLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} />}
          {pricingLoading ? "Generowanie..." : "Generuj cennik AI"}
        </Button>
        {items.length > 0 && !editing && (
          <Button variant="outline" size="sm" className="gap-1 text-xs" onClick={startEditing}>
            <Pencil className="w-3 h-3" /> Edytuj ceny
          </Button>
        )}
        {editing && (
          <>
            <Button size="sm" className="gap-1 text-xs" onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <DollarSign className="w-3 h-3" />}
              Zapisz cennik
            </Button>
            <Button variant="outline" size="sm" className="gap-1 text-xs" onClick={refreshCategories} disabled={loadingCats}>
              {loadingCats ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              {site?.site_type === "emergent" ? "Aktualizuj kategorie" : "Aktualizuj kategorie WP"}
            </Button>
            <Button variant="ghost" size="sm" className="text-xs" onClick={() => setEditing(false)}>Anuluj</Button>
          </>
        )}
      </div>

      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground text-center py-8">Brak cennika. Wygeneruj cennik AI.</p>
      ) : (
        <>
          <p className="text-[10px] text-muted-foreground">{items.length} pozycji cennikowych | {items.filter(x=>x.show_in_shop!==false).length} widocznych w sklepie</p>
          <div className="space-y-1">
            {items.map((item, i) => (
              <div key={i} data-testid={`pricing-item-${i}`}
                className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                  item.enabled ? "bg-card border-border" : "bg-muted/30 border-border/50 opacity-60"
                }`}>
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-medium ${item.enabled ? "text-foreground" : "text-muted-foreground"}`}>{item.name}</p>
                  {item.tags && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {(Array.isArray(item.tags) ? item.tags : []).slice(0, 3).map((tag, ti) => (
                        <Badge key={ti} variant="secondary" className="text-[8px] rounded-md">{tag}</Badge>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {editing ? (
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2">
                        <Input type="number" value={item.price_pln} onChange={(e) => updatePrice(i, e.target.value)}
                          className="w-20 h-8 text-right text-xs font-bold" />
                        <span className="text-[10px] text-muted-foreground">PLN</span>
                        <div className="flex flex-col items-center gap-0.5">
                          <button onClick={() => toggleEnabled(i)} title="Aktywna"
                            className={`w-7 h-4 rounded-full transition-colors ${item.enabled ? "bg-emerald-500" : "bg-slate-300"}`}>
                            <div className={`w-3 h-3 rounded-full bg-white transition-transform ${item.enabled ? "translate-x-3" : "translate-x-0.5"}`} />
                          </button>
                          <span className="text-[7px] text-muted-foreground">ON</span>
                        </div>
                        <div className="flex flex-col items-center gap-0.5">
                          <button onClick={() => toggleShop(i)} title="Widoczna w sklepie"
                            className={`w-7 h-4 rounded-full transition-colors ${item.show_in_shop ? "bg-blue-500" : "bg-slate-300"}`}>
                            <div className={`w-3 h-3 rounded-full bg-white transition-transform ${item.show_in_shop ? "translate-x-3" : "translate-x-0.5"}`} />
                          </button>
                          <span className="text-[7px] text-muted-foreground">Sklep</span>
                        </div>
                      </div>
                      {/* Kategorie WP - multi-select z wyszukiwaniem */}
                      <WpCategoryPicker
                        categories={wpCategories}
                        selected={item.wp_category_ids || (item.wp_category_id ? [item.wp_category_id] : [])}
                        onChange={(ids, names) => setItems(prev => prev.map((it, idx) =>
                          idx === i ? {...it, wp_category_ids: ids, wp_category_names: names} : it
                        ))}
                      />
                    </div>
                  ) : (
                    <div className="flex flex-col items-end gap-0.5">
                      <span className="text-sm font-bold text-primary">{item.price_pln} PLN</span>
                      {(item.wp_category_names || []).length > 0 && (
                        <div className="flex flex-wrap gap-0.5 justify-end max-w-[150px]">
                          {item.wp_category_names.map((n, ni) => (
                            <span key={ni} className="text-[7px] bg-blue-100 text-blue-600 px-1 rounded">{n}</span>
                          ))}
                        </div>
                      )}
                      {item.wp_category_name && !(item.wp_category_names || []).length && (
                        <span className="text-[8px] text-blue-500">{item.wp_category_name}</span>
                      )}
                      {!item.show_in_shop && <Badge className="text-[7px] bg-slate-100 text-slate-500 border-0">Ukryta</Badge>}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {site.pricing_at && (
        <p className="text-[10px] text-muted-foreground/60">
          Cennik z: {new Date(site.pricing_at).toLocaleString()}
        </p>
      )}
    </div>
  );
}

// Komponent WpCategoryPicker - multi-select z wyszukiwaniem
function WpCategoryPicker({ categories, selected, onChange }) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);

  const filtered = (categories || []).filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  const toggleCategory = (cat) => {
    const isSelected = selected.some(id => String(id) === String(cat.id));
    let newIds, newNames;
    if (isSelected) {
      newIds = selected.filter(id => String(id) !== String(cat.id));
      newNames = categories.filter(c => newIds.includes(String(c.id))).map(c => c.name);
    } else {
      newIds = [...selected, String(cat.id)];
      newNames = categories.filter(c => newIds.includes(String(c.id))).map(c => c.name);
    }
    onChange(newIds, newNames);
  };

  const selectedNames = categories.filter(c => selected.includes(String(c.id))).map(c => c.name);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-2 px-2 py-1.5 text-[10px] border rounded bg-background hover:bg-accent/50 transition-colors"
      >
        <span className="truncate text-left">
          {selectedNames.length > 0 ? selectedNames.join(", ") : "Kategorie WP..."}
        </span>
        <Tag className="w-3 h-3 text-muted-foreground shrink-0" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-64 bg-popover border rounded-lg shadow-lg p-2">
          <Input
            placeholder="Szukaj kategorii..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-7 text-xs mb-2"
            autoFocus
          />
          <ScrollArea className="max-h-48">
            {filtered.length === 0 ? (
              <p className="text-[10px] text-muted-foreground text-center py-2">Brak kategorii</p>
            ) : (
              <div className="space-y-0.5">
                {filtered.map((cat) => {
                  const isSelected = selected.some(id => String(id) === String(cat.id));
                  return (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => toggleCategory(cat)}
                      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left transition-colors ${
                        isSelected ? "bg-primary/10 text-primary" : "hover:bg-accent"
                      }`}
                    >
                      <div className={`w-3.5 h-3.5 border rounded flex items-center justify-center ${
                        isSelected ? "bg-primary border-primary" : "border-muted-foreground/30"
                      }`}>
                        {isSelected && <span className="text-[8px] text-primary-foreground">✓</span>}
                      </div>
                      <span className="text-[10px] truncate flex-1">{cat.name}</span>
                      <span className="text-[8px] text-muted-foreground">({cat.count})</span>
                    </button>
                  );
                })}
              </div>
            )}
          </ScrollArea>
          <div className="flex justify-end mt-2 pt-2 border-t">
            <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => setOpen(false)}>
              Zamknij
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

